/* eslint-disable max-len */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-unresolved */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MoreVert } from '@mui/icons-material';
import { Avatar, IconButton, List, ListItem, ListItemIcon, ListItemText, Menu, Tooltip } from '@mui/material';
import { isEmpty, isNull, isUndefined, orderBy } from 'lodash';
import { Link, useNavigate } from 'react-router-dom';
import { Firm } from 'api';
import { companiesAction, fundsAction, globalAction } from 'common/actions';
import { ERROR_403 } from 'common/config/api';
import { firmCompGroupsUrl, firmSummaryUrl, userProfileTabUrl } from 'common/config/urls';
import { CREATE_FIRM_ENABLER } from 'common/constants/features';
import {
  FILTER_FIRM_NUMBER,
  FIRMS_MENU_SLUG,
  ITEM_HEIGHT,
  SELECT_A_FIRM,
  SELECT_FIRM,
  USER_MENU_SLUG,
} from 'common/constants/sidebar';
import { ACCOUNT_SETTINGS_TAB } from 'common/constants/user';
import { PINNED_FIRMS_KEY } from 'common/constants/userPreferences';
import { useLocalStorageByUser } from 'common/hooks';
import { useStore } from 'common/store';
import FeatureEnabler from 'components/FeatureEnabler';
import FeaturesContext from 'context/FeaturesContext';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { PushPin } from 'icons';
import { ShowFeedbackWidget } from 'providers';
import { useLogOutUser } from 'services/hooks/auth';
import { getInitials } from 'utilities';
import FirmFilter from '../FirmFilter';
import { BottomMenuItemsProps } from './BottomMenuItems.props';
import {
  OverflowText,
  StyledAvatar,
  StyledBottomMenuSubMenuIcon,
  StyledLink,
  StyledMenuItem,
  StyledMenuItemsLabel,
  StyledSecondaryLink,
  StyleOutlinedPushPin,
} from './BottomMenuItems.styles';
import AsyncJobProcessingIndicator from '../../../../../../components/Notifications/AsyncJobProcessingIndicator/AsyncJobProcessingIndicator';
import { useFindPath } from '../../utilities/useFindPath';
import AddFirmItem from '../AddFirmItem/AddFirmItem';
import BottomMenuItemsSkeleton from '../BottomMenuItemsSkeleton';
import { MenuItem as MenuItemType, SubMenuItem } from '../type';

const BottomMenuItems = ({ setOpenSidebar, open }: BottomMenuItemsProps) => {
  const navigate = useNavigate();
  const [{ firmList, firmId, user, firmInfo }, dispatch] = useStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [filterSearch, setFilterSearch] = useState<string>('');
  const [pinnedFirms, setPinnedFirms] = useLocalStorageByUser(PINNED_FIRMS_KEY, []);
  const logOutUser = useLogOutUser();
  const { setAction } = useContext(UnsavedChangesContext);
  const { features } = useContext(FeaturesContext);

  const path = useFindPath();

  const updatePinStatus = useCallback(
    (id: number, title: string, isPinned: boolean) => {
      let tmpPinnedOptions: any = {};

      if (isPinned) {
        tmpPinnedOptions = pinnedFirms.filter((o: any) => o.value !== id) ?? [];
      } else {
        tmpPinnedOptions = [...pinnedFirms, { value: id, label: title }];
      }

      setPinnedFirms(tmpPinnedOptions);
    },
    [pinnedFirms, setPinnedFirms]
  );

  useEffect(() => {
    if (!open) {
      // If the dialog is false
      if (isNull(anchorEl)) {
        // ...and if firm list it's not open (without anchorEl)
        setFilterSearch('');
      } else {
        // ...and anchorEl exist
        setAnchorEl(null);
      }
    }
  }, [open, anchorEl]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // Do not show confirmation dialog if there are pending changes but the user wants to leave
    setAction(false);
    logOutUser();
  };

  const createFirmFeatureStatus = useMemo(() => {
    if (features && CREATE_FIRM_ENABLER in features) {
      return features[CREATE_FIRM_ENABLER];
    }
  }, [features]);

  const handleFirmSelection = (firm: Firm) => {
    if (firm?.id && firm.slug) {
      // Navigate to firm summary
      dispatch(companiesAction.setCompanyList(null));
      dispatch(fundsAction.setFundList(null));
      navigate(firmSummaryUrl(firm.slug));
    }

    handleMenuClose();
    setOpenSidebar?.(false);
  };

  const handleAddFirm = () => {
    handleMenuClose();
    setOpenSidebar?.(false);
    dispatch(globalAction.showFirmStepper(true));
  };

  const selectedFirm = useMemo(() => {
    if (isEmpty(firmList) || !firmId) {
      return SELECT_A_FIRM;
    }
    return firmList.find((firm: Firm) => firm.id === firmId);
  }, [firmList, firmId]);

  const notSelectedFirms = useMemo((): SubMenuItem[] => {
    let tmpList: SubMenuItem[] = [];
    if (isEmpty(firmList) || firmList === ERROR_403) {
      tmpList = [];
    } else if (firmList.length && firmId) {
      let filteredFirm: SubMenuItem[] = [];
      // Get the list of firms without the selected one
      filteredFirm = firmList
        .filter((firm: Firm) => firm.id !== firmId)
        .map((firm: Firm) => {
          const isPinned = Boolean(pinnedFirms.find((pinnedFirm: any) => pinnedFirm.value === firm.id));

          return {
            id: firm.id,
            title: firm.name,
            slug: firm.slug,
            to: '/#',
            disabled: false,
            isPinnable: true,
            isPinned,
            updatePinStatus: (id: number, title: string, isPinned: boolean) => updatePinStatus(id, title, isPinned),
            callback: () => handleFirmSelection(firm),
          };
        });

      if (filterSearch) {
        filteredFirm = filteredFirm.filter(firm => {
          const firmTitle = firm.title.toLowerCase();
          return firmTitle.includes(filterSearch);
        });
      }
      if (filterSearch && !filteredFirm.length) {
        filteredFirm.push({
          title: 'Not found',
          disabled: true,
        });
      }

      // Sort by pinned status (pinned items first)
      return orderBy(filteredFirm, ['isPinned'], ['desc']);
    }
    if (typeof path === 'string' && path === firmCompGroupsUrl('0')) {
      return [];
    }
    return tmpList;
  }, [firmList, firmId, filterSearch, path, pinnedFirms, updatePinStatus]);

  const items: SubMenuItem[] = [];

  if (firmInfo?.slug) {
    items.push({
      title: 'Account Settings',
      slug: 'account-settings',
      url: () => `${userProfileTabUrl(firmInfo.slug!, ACCOUNT_SETTINGS_TAB)}`,
      callback: () => {
        handleMenuClose();
        setOpenSidebar?.(false);
      },
    });
  }

  if (process.env.REACT_APP_SCALAR_FEEDBACK_API_PROJECT_ID) {
    items.push({
      title: 'Feedback',
      slug: 'feedback',
      callback: () => {
        ShowFeedbackWidget(user);
        handleMenuClose();
        setOpenSidebar?.(false);
      },
    });
  }

  const userMenuItems = useMemo(() => items, [firmInfo]);

  const menuItems = useMemo((): MenuItemType[] => {
    if (isEmpty(selectedFirm) || isEmpty(user) || (isEmpty(userMenuItems) && firmInfo?.slug)) {
      return [];
    }

    const userSection: MenuItemType = {
      textPrimary: user.name ?? user.username ?? '',
      textSecondary: (
        <StyledSecondaryLink as={Link} to="#" onClick={handleLogout}>
          Log Out
        </StyledSecondaryLink>
      ),
      slug: USER_MENU_SLUG,
      avatar: (
        <StyledAvatar open={open}>
          <Avatar src={user.profile?.picture_url} />
        </StyledAvatar>
      ),
      subItems: userMenuItems,
    };

    return selectedFirm?.slug
      ? [
        {
          textPrimary: selectedFirm?.name ?? SELECT_A_FIRM,
          textSecondary: selectedFirm?.slug && (
            <StyledSecondaryLink as={Link} to={firmSummaryUrl(selectedFirm.slug)}>
                View Details
            </StyledSecondaryLink>
          ),
          slug: FIRMS_MENU_SLUG,
          avatar: <StyledAvatar open={open}>{getInitials({ name: selectedFirm.name })}</StyledAvatar>,
          subItems: notSelectedFirms,
          subItemsLabel:
              firmList?.length > FILTER_FIRM_NUMBER ? <FirmFilter setFilterSearch={setFilterSearch} /> : SELECT_FIRM,
        },
        userSection,
      ]
      : [userSection];
  }, [selectedFirm, notSelectedFirms, user, userMenuItems]);

  const shouldDisplayMoreVert = (menuItem: MenuItemType) =>
    user.is_superuser ||
    createFirmFeatureStatus ||
    menuItem.slug === USER_MENU_SLUG ||
    (menuItem.subItems && menuItem.subItems.length > 0);

  const getLinkDestination = useCallback(
    (option: SubMenuItem): string => {
      if (!(option.to || option.url)) {
        return '#';
      }

      const urlValue = typeof option.url === 'function' ? option.url() : option.url;

      // || here as empty string is falsy
      return urlValue || firmSummaryUrl(option.slug!);
    },
    [firmSummaryUrl]
  );

  const displayAddFirm = () => {
    if (user.is_superuser) {
      return <AddFirmItem handleAddFirm={handleAddFirm} />;
    }
    return (
      <FeatureEnabler enabler={CREATE_FIRM_ENABLER} hideComponent>
        <AddFirmItem handleAddFirm={handleAddFirm} />
      </FeatureEnabler>
    );
  };

  if (isUndefined(notSelectedFirms) || (isEmpty(userMenuItems) && firmInfo?.slug)) {
    return <BottomMenuItemsSkeleton />;
  }

  return (
    <List style={{ marginBottom: '4rem' }}>
      <AsyncJobProcessingIndicator />
      {menuItems.map((menuItem: MenuItemType) => (
        <div key={menuItem.slug} id={`sidebar-bottom__menu-${menuItem.slug}`}>
          <ListItem>
            <ListItemIcon className="sidebar-bottom__menu-icon">{menuItem.avatar}</ListItemIcon>
            <ListItemText
              primary={menuItem.textPrimary}
              secondary={menuItem.textSecondary}
              slotProps={{
                primary: {
                  sx: {
                    ...OverflowText,
                    color: 'white',
                    fontWeight: 700,
                  },
                },
                secondary: {
                  sx: OverflowText,
                },
              }}
              className="sidebar-bottom__menu-text"
            />
            {open && shouldDisplayMoreVert(menuItem) && (
              <IconButton
                id={`${menuItem.slug}-button`}
                aria-label="more"
                aria-controls={menuItem.slug}
                aria-haspopup="true"
                component="span"
                onClick={handleMenuClick}
                className="sidebar-bottom__menu-button">
                <MoreVert as={StyledBottomMenuSubMenuIcon} fontSize="small" />
              </IconButton>
            )}
          </ListItem>
          {((menuItem?.subItems && menuItem?.subItems?.length > 0) || createFirmFeatureStatus) && (
            <Menu
              id={`sidebar-bottom__dropdown-${menuItem.slug}`}
              anchorEl={anchorEl}
              open={open && anchorEl ? anchorEl.getAttribute('aria-controls') === menuItem.slug : false}
              onClose={handleMenuClose}
              PaperProps={{
                style: {
                  maxHeight: ITEM_HEIGHT,
                },
                square: true,
              }}>
              {menuItem.subItemsLabel && menuItem.subItems && menuItem.subItems.length > 0 && (
                <StyledMenuItemsLabel>{menuItem.subItemsLabel}</StyledMenuItemsLabel>
              )}
              {menuItem.subItems &&
                menuItem.subItems.map((option: SubMenuItem) => (
                  <div key={option.slug}>
                    {/* Take into account individual firms */}
                    {option.disabled ? (
                      <StyledMenuItem disabled>{option.title}</StyledMenuItem>
                    ) : (
                      <StyledLink to={getLinkDestination(option)}>
                        <ListItem
                          component="button"
                          className={`sidebar-bottom__dropdown-item sidebar-bottom__dropdown-item-${option.slug}`}
                          sx={{ width: '100%' }}
                          onClick={option.callback}>
                          {option.isPinnable && (
                            <Tooltip title={option.isPinned ? 'Unpin' : 'Pin'}>
                              <IconButton
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  if (option.updatePinStatus && option.id) {
                                    option.updatePinStatus(option.id, option.title, option.isPinned || false);
                                  }
                                }}
                                size="small"
                                aria-label="pin"
                                style={{ marginRight: '8px' }}>
                                {option.isPinned ? (
                                  <PushPin fontSize="small" htmlColor="rgba(0, 0, 0, 0.6)" />
                                ) : (
                                  <StyleOutlinedPushPin fontSize="small" htmlColor="rgba(0, 0, 0, 0.6)" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          <ListItemText primary={option.title} />
                        </ListItem>
                      </StyledLink>
                    )}
                  </div>
                ))}
              {menuItem.slug === FIRMS_MENU_SLUG && displayAddFirm()}
            </Menu>
          )}
        </div>
      ))}
    </List>
  );
};

export default BottomMenuItems;
